import { Button, Modal, notification } from "antd";
import React, { FC } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Title from "antd/es/typography/Title";
import { useFormikContext } from "formik";
import { FormInput, FormSelect } from "shared/ui";
import { handleRequestError } from "shared/helpers";
import { useUpdateMachineMutation } from "shared/api";
import { AdaptedValues, AddMachinesValues } from "shared/types";
import { ModalSizeValue } from "shared/constants";
import { Routes } from "shared/routers";

interface EditMachineModalProps {
  isOpenModal: boolean;
  hideEditMachineModal: () => void;
  adaptedProcessTemplates: AdaptedValues[];
}

const EditMachineModal: FC<EditMachineModalProps> = ({
  isOpenModal,
  hideEditMachineModal,
  adaptedProcessTemplates,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [api, contextHolder] = notification.useNotification();

  const {
    values,
    resetForm,
    handleBlur,
    handleChange,
    isValid,
    setFieldValue,
    errors,
  } = useFormikContext<AddMachinesValues>();

  const closeModal = () => {
    hideEditMachineModal();
    resetForm();
  };

  const [updateMachine] = useUpdateMachineMutation();

  const goToMachineHistory = () => {
    if (!values.name) return;
    navigate(
      `${Routes.taskHubUserAllTasks.url}?machine=${encodeURIComponent(
        values.name
      )}`
    );
  };

  const updateMachineRequest = async () => {
    try {
      await updateMachine({
        id: values.id!,
        name: values.name!,
        process_template: values.process_template!,
      }).unwrap();
      api.success({
        message: t("machines.success-title"),
        description: t("machines.success-update-msg"),
      });
      closeModal();
    } catch (error) {
      const description = handleRequestError(error, t("machines.error"));

      api.error({
        message: t("machines.error"),
        description,
      });
    }
  };

  const themeColor = JSON.parse(
    localStorage.getItem("color-theme") || '"#002F5F"'
  );

  return (
    <>
      {contextHolder}
      <Modal
        title={t("machines.edit-title")}
        open={isOpenModal}
        width={ModalSizeValue.PRIMARY}
        okButtonProps={{
          disabled: !isValid,
          style: {
            background: `${themeColor}`,
          },
        }}
        onCancel={closeModal}
        onOk={() => {
          updateMachineRequest();
        }}
      >
        <div className="flex items-center justify-between mb-1">
          <Title level={5} className="mb-1.25">
            {t("machines.input-title")}
          </Title>

          <Button onClick={goToMachineHistory}>See history</Button>
        </div>

        <FormInput
          value={values.name}
          fieldName="name"
          placeholder={t("machines.machine-placeholder")}
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.name}
          isError={!!errors.name}
        />

        <Title level={5}> {t("machines.select-title")}</Title>

        <FormSelect
          value={values.process_template}
          placeholder={t("machines.process-type-placeholder")}
          fieldName="process_template"
          options={adaptedProcessTemplates}
          setFieldValue={setFieldValue}
          handleBlur={handleBlur}
          error={errors.process_template?.toString()}
          isError={!!errors.process_template}
        />
      </Modal>
    </>
  );
};

export { EditMachineModal };
